import {
  DEFAULT_CHART_DIMENSIONS,
  TimeRange,
  type ChartWindowState,
} from '@ardoq/graph';
import type { Point } from '@ardoq/math';
import {
  GridPrecisionAutoOptionValue,
  TimelineGridPrecisionOptionValue,
  TimelinesGridPrecisionUnit,
  UnitOfTime,
} from '@ardoq/api-types';
import { MAX_DATE, MIN_DATE } from '@ardoq/date-range';
import { TimelineChartDimensions } from './types';

// data-popover-element-type
export enum POPOVER_ELEMENT_TYPE {
  PHASE = 'phase',
  MILESTONE = 'milestone',
  MAIN_LABEL = 'main-label',
  OTHER_LABEL = 'other-label',
}

/**
 * At least 3 letters of the deepest label should be visible
 */
export const MAX_MIN_VISIBLE_LABEL_WIDTH = 25;

/**
 * At least 3 letters both for titles and values of additional labels should be visible
 */
export const MAX_MIN_VISIBLE_LABEL_WIDTH_MODERNIZED = 55;

export const DEFAULT_WINDOW_CENTER: Point = [0.5, 0.5];

export const DEFAULT_CHART_WINDOW_STATE: ChartWindowState = {
  windowCenter: DEFAULT_WINDOW_CENTER,
  windowScale: 1.0,
};

export const TIMELINES_GRID_PRECISION_AUTO_OPTION_VALUE: GridPrecisionAutoOptionValue =
  '*';

export type GridPrecisionOptionLabels = {
  autoLayout?: string;
  daily?: string;
  weekly?: string;
  monthly?: string;
  quarterly?: string;
  yearly?: string;
};

export const getTimelinesGridPrecisionOptions = ({
  autoLayout = 'Auto-layout',
  daily = 'Daily',
  weekly = 'Weekly',
  monthly = 'Monthly',
  quarterly = 'Quarterly',
  yearly = 'Yearly',
}: GridPrecisionOptionLabels = {}): [
  value: TimelineGridPrecisionOptionValue,
  label: string,
][] => [
  [TIMELINES_GRID_PRECISION_AUTO_OPTION_VALUE, autoLayout],
  [UnitOfTime.DAY, daily],
  [UnitOfTime.WEEK, weekly],
  [UnitOfTime.MONTH, monthly],
  [UnitOfTime.QUARTER, quarterly],
  [UnitOfTime.YEAR, yearly],
];

export const TIMELINES_GRID_PRECISION_OPTIONS: [
  value: TimelineGridPrecisionOptionValue,
  label: string,
][] = getTimelinesGridPrecisionOptions();

const ONE_SECOND = 1000;
const ONE_MINUTE = ONE_SECOND * 60;
const ONE_HOUR = ONE_MINUTE * 60;
const ONE_DAY = ONE_HOUR * 24;
const ONE_YEAR = ONE_DAY * 365;
const ONE_WEEK = ONE_DAY * 7;
const ONE_MONTH = 30 * ONE_DAY;
const ONE_QUARTER = 3 * ONE_MONTH;

export const unitQuantities: Record<UnitOfTime, number> = {
  [UnitOfTime.MILLISECOND]: 1,
  [UnitOfTime.SECOND]: ONE_SECOND,
  [UnitOfTime.MINUTE]: ONE_MINUTE,
  [UnitOfTime.HOUR]: ONE_HOUR,
  [UnitOfTime.DAY]: ONE_DAY,
  [UnitOfTime.WEEK]: ONE_WEEK,
  [UnitOfTime.MONTH]: ONE_MONTH,
  [UnitOfTime.QUARTER]: ONE_QUARTER,
  [UnitOfTime.YEAR]: ONE_YEAR,
};

export const expandUnits = new Map<number, UnitOfTime>([
  [ONE_YEAR, UnitOfTime.YEAR],
  [ONE_QUARTER, UnitOfTime.QUARTER],
  [ONE_MONTH, UnitOfTime.MONTH],
  [ONE_WEEK, UnitOfTime.WEEK],
  [ONE_DAY, UnitOfTime.DAY],
]);

/**
 * min distance between grid lines in px
 */
export const minGridLineDistances: Record<TimelinesGridPrecisionUnit, number> =
  {
    [UnitOfTime.DAY]: 56,
    [UnitOfTime.WEEK]: 24,
    [UnitOfTime.MONTH]: 96,
    [UnitOfTime.QUARTER]: 24,
    [UnitOfTime.YEAR]: 48,
  };

/**
 * This answers the question, how far user can zoom in. This is the min amount of grid units
 * that should fit into chart window
 */
export const minimumDisplayedGridUnitsForGridPrecision: Record<
  TimelinesGridPrecisionUnit,
  number
> = {
  [UnitOfTime.DAY]: 2,
  [UnitOfTime.WEEK]: 2,
  [UnitOfTime.MONTH]: 2,
  [UnitOfTime.QUARTER]: 2,
  [UnitOfTime.YEAR]: 2,
};

export const DEFAULT_TIMELINE_SCALE_LIMITS = {
  max: 1,
  min: 0.1,
};

export const DEFAULT_TIME_RANGE: TimeRange = [MIN_DATE, MAX_DATE];

export const EMPTY_CHART_DIMENSIONS: TimelineChartDimensions = {
  dimensions: DEFAULT_CHART_DIMENSIONS,
  availableExpandedDomain: DEFAULT_TIME_RANGE,
  labelsColumnMinWidth: 0,
};
